import type { UnknownAction } from '@reduxjs/toolkit';
import type { AxiosResponse } from 'axios';
import type { Map as ImmutableMap, RecordOf } from 'immutable';
import type { SagaIterator } from 'redux-saga';
import { call, delay, put, select, takeEvery, takeLeading } from 'redux-saga/effects';

import { accountsActions, accountsTypes } from 'app-components/flux/accounts';
import type { AccountType } from 'app-components/flux/accounts/accountsTypes';
import { resourceSagaUtils, resourceStoreTypes } from 'app-components/flux/core';
import type { Meta } from 'app-components/flux/core/actionHelpers';
import { getLogger, httpClientInterface, requireEnvironmentConfig } from 'app-components/utils/core';

import * as actions from './actions';
import { getFundingAccountsById, getLastSyncDate } from './selectors';
import type { FundingAccount, FundingAccountProps, VerifyMicrodepositsResponse } from './types';
import { mkFundingAccount, statusEnum, subStatusEnum } from './types';

const log = getLogger('data/fundingAccount/fundingAccountSagas.ts');

// The list endpoint has answered with both `data` and `resources`.
type FundingAccountList = { resources?: FundingAccountProps[]; data?: FundingAccountProps[] };

const mkQcsSyncFundingAccountsConfig =
  resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<FundingAccountProps>();

export function* getFundingAccountsActionWatcher() {
  const config = mkQcsSyncFundingAccountsConfig({
    resourceName: 'fundingAccount',
    resourceBaseUrl: '/aikya-funding-account/api/v2/funding-accounts',
    getLastSyncDate,
    successAction: actions.getFundingAccountsSuccess,
    failureAction: actions.getFundingAccountsFailure,
    transformResponseToResources: (
      response?: { data?: FundingAccountList } | null,
    ): Array<RecordOf<FundingAccountProps>> | undefined => {
      const list = response?.data?.resources ?? response?.data?.data ?? [];
      return list?.map((fundingAcc) => mkFundingAccount(fundingAcc));
    },
  }) as resourceStoreTypes.QcsSyncResourcesConfig<FundingAccountProps>;
  yield takeLeading(
    actions.getFundingAccounts.type,
    resourceSagaUtils.qcsSyncGetResources<FundingAccountProps>,
    config,
  );
}

// poll every 4 seconds up to a total of 16 seconds
const FUNDING_ACCOUNT_POLL_INTERVAL = 4;
const FUNDING_ACCOUNT_POLL_MAX_TIME = 16;
const FUNDING_ACCOUNT_POLL_ATTEMPTS = FUNDING_ACCOUNT_POLL_MAX_TIME / FUNDING_ACCOUNT_POLL_INTERVAL;
export function* pollForVerification(
  payload: FundingAccount,
  failureAction: (error: unknown) => UnknownAction,
  meta?: Meta,
): Generator {
  const fundingAccountId = payload.id;
  let response: AxiosResponse<FundingAccountList> | undefined;
  let timeout = FUNDING_ACCOUNT_POLL_ATTEMPTS;
  while (timeout > 0) {
    try {
      log.log(`Number of times left to poll is ${timeout} #funding-account-verification`);
      response = yield call(
        httpClientInterface.send,
        'GET',
        `${requireEnvironmentConfig().services_url}/aikya-funding-account/api/v2/funding-accounts`,
      );
      log.log('#funding-account-verification, poll response:', response);
      const rawList = response?.data?.data ?? response?.data?.resources ?? [];
      const fundedList: RecordOf<FundingAccountProps>[] = rawList.map(mkFundingAccount);
      const current = fundedList.find((fa) => fa.id === fundingAccountId) as FundingAccount | undefined;
      if (current?.status === statusEnum.VERIFIED) {
        meta?.resolve?.(current);
        return;
      }
    } catch (e) {
      log.warn('failure in funding account verification polling\n', e);
      yield put(failureAction(e));
      meta?.reject?.(e);
      return;
    }
    timeout -= 1;
    yield delay(FUNDING_ACCOUNT_POLL_INTERVAL * 1000);
  }
  // time-out
  log.log('Time-out #funding-account-verification');
  meta?.resolve?.(payload);
}

function* doCreate(
  payload: FundingAccount | FundingAccountProps,
  userSetAccountType: AccountType,
  reject?: ((error: unknown) => void) | undefined,
): SagaIterator<FundingAccount | null> {
  try {
    const requestUrl = `${
      requireEnvironmentConfig().services_url
    }/aikya-funding-account/api/v2/funding-account?intent=funds-transfer`;
    const response: AxiosResponse<FundingAccountProps> = yield call(
      httpClientInterface.send,
      'POST',
      requestUrl,
      'toJS' in payload ? payload.toJS() : payload, // transpose to JS obj if payload is record
    );
    log.log('FundingAccount create, response=', response);
    const newFundingAccount = mkFundingAccount(response.data); // mk record before sending to reducer

    yield put(actions.createFundingAccountSuccess(newFundingAccount));
    // add FA id to account
    yield put(
      accountsActions.updateAccount(
        accountsTypes.mkUpdateAccount(userSetAccountType, {
          id: newFundingAccount.linkedAccountId, // ONLY used here. Not reliable for anything else!!!
          fundingAccountId: newFundingAccount.id,
        }),
      ),
    );
    return newFundingAccount;
  } catch (e) {
    log.warn('Error in fundingAccount create request\n', e);
    reject?.(e);
  }
  return null;
}

const lastFour = (numStr?: string) => (numStr ? numStr.slice?.(-4) : '');

export function* createFundingAccountActionWatcher() {
  yield takeEvery(
    actions.createFundingAccount.type,
    function* handle(action: ReturnType<typeof actions.createFundingAccount>) {
      try {
        const { payload } = action;
        const { fundingAccount, userSetAccountType } = payload;
        const fundingAccounts: ImmutableMap<string, FundingAccount> = yield select(getFundingAccountsById);

        // TODO: Matching accounts currently does not work properly because we are not altering the funding account values appropriately and might even
        // TODO: connect to an older incorrect account that was made manual. We need to account for this scenario
        // check to make sure we don't have an orphaned funding account already present
        const matchingAccount = fundingAccounts.find(
          (acc) =>
            lastFour(acc?.accountNumber) === lastFour(fundingAccount?.accountNumber) &&
            lastFour(acc?.routingNumber) === lastFour(fundingAccount?.routingNumber),
        );

        let fundingAccountToConfirm: FundingAccount | null = null;
        if (matchingAccount?.id) {
          log.log('matching funding account found:\n', matchingAccount, '\nLinking to existing funding account');

          if (matchingAccount.linkedAccountId === fundingAccount.linkedAccountId) {
            // add orphaned FA to account
            yield put(
              accountsActions.updateAccount(
                accountsTypes.mkUpdateAccount(userSetAccountType, {
                  id: fundingAccount.linkedAccountId,
                  fundingAccountId: matchingAccount.id,
                }),
              ),
            );
            fundingAccountToConfirm = matchingAccount;
          } else {
            // funding account has outdated linkedAccountId
            yield put(actions.deleteFundingAccount({ id: matchingAccount.id, isDeleted: true }));
            yield delay(3000); // some time for funding account to be cleared on QCS
            fundingAccountToConfirm = yield doCreate(fundingAccount, userSetAccountType, action.meta?.reject);
          }
        } else {
          fundingAccountToConfirm = yield doCreate(fundingAccount, userSetAccountType, action.meta?.reject);
        }

        log.log('FundingAccount to confirm:', fundingAccountToConfirm);
        if (fundingAccountToConfirm && fundingAccountToConfirm.status === statusEnum.SUBMITTED) {
          log.log('Beginning Funding confirmation');
          yield put(actions.confirmFundingAccount(fundingAccountToConfirm, action.meta));
        } else {
          action.meta?.resolve?.(fundingAccountToConfirm);
        }
      } catch (e) {
        log.warn('Error in fundingAccount createHandler\n', e);
        yield put(actions.createFundingAccountFailure(e));
        action.meta?.reject?.(e);
      }
    },
  );
}

export function* confirmFundingAccountActionWatcher() {
  yield takeEvery(
    actions.confirmFundingAccount.type,
    function* handle(action: ReturnType<typeof actions.confirmFundingAccount>) {
      try {
        const { payload } = action;
        const requestUrl = `${requireEnvironmentConfig().services_url}/aikya-funding-account/api/v2/funding-account/${
          payload.id
        }{/confirm?intent=funds-transfer`;
        const response: AxiosResponse<unknown> = yield call(
          httpClientInterface.send,
          'PUT',
          requestUrl,
          null, // info appeneded to the requestURL
        );
        log.log('FundingAccount confirm, response=', response);
        yield delay(100); // QCS time to update DB
        yield put(actions.getFundingAccounts()); // GET because that has proper verification status enum
        yield delay(2000); // some time for funding account to be retrieved QCS before updating back to the UI
        if (
          payload.subStatus === subStatusEnum.NO_OP &&
          (payload.status === statusEnum.VERIFICATION_IN_PROGRESS || payload.status === statusEnum.SUBMITTED)
        ) {
          yield* pollForVerification(payload, actions.confirmFundingAccountFailure, action.meta);
        } else {
          action.meta?.resolve?.(payload);
        }
      } catch (e) {
        log.warn('Error in fundingAccount confirm request\n', e);
        yield put(actions.confirmFundingAccountFailure(e));
        // GET to ensure we have the most updated eligibility + verification status
        yield put(actions.getFundingAccounts()); // GET to ensure we have the most updated eligibility + verification status
        action.meta?.reject?.(e);
      }
    },
  );
}

export function* verifyMicroDespositsActionWatcher() {
  yield takeEvery(
    actions.verifyMicroDepositsAction.type,
    function* handle(action: ReturnType<typeof actions.verifyMicroDepositsAction>) {
      try {
        const { id, amountOne, amountTwo } = action.payload;
        const requestUrl = `${
          requireEnvironmentConfig().services_url
        }/aikya-funding-account/api/v2/account-verification/${id}/verify-micro-deposits`;
        const response: AxiosResponse<VerifyMicrodepositsResponse> = yield call(
          httpClientInterface.send,
          'PUT',
          requestUrl,
          {
            amount_one: amountOne,
            amount_two: amountTwo,
          },
        );
        log.log('Verify MicroDeposits, response=', response);
        // TODO: maybe check response code before doing a fresh GET?
        yield put(actions.getFundingAccounts()); // GET because that has proper verification status enum
        action.meta?.resolve?.(response);
      } catch (e) {
        log.warn('Error in verify micoDeposits request\n', e);
        yield put(actions.verifyMicroDepositsActionFailure(e));
        action.meta?.reject?.(e);
      }
    },
  );
}

export function* deleteFundingAccountActionWatcher() {
  yield takeEvery(
    actions.deleteFundingAccount.type,
    function* handle(action: ReturnType<typeof actions.deleteFundingAccount>) {
      try {
        const { id } = action.payload;
        const requestUrl = `${requireEnvironmentConfig().services_url}/aikya-funding-account/api/v1/funding-account/${id}`; // maybe add other params?
        const response: AxiosResponse<unknown> = yield call(httpClientInterface.send, 'DELETE', requestUrl, undefined);
        log.log('Delete FundingAccount, response=', response);
        // QCS isn't returning the deleted resource, so we have to prune ourselves
        const fundingAccounts: ImmutableMap<string, FundingAccount> = yield select(getFundingAccountsById);
        const deletedFundingAccount = fundingAccounts?.get(id)?.set('isDeleted', true);
        if (deletedFundingAccount) {
          yield put(actions.deleteFundingAccountSuccess(deletedFundingAccount));
        }
        action.meta?.resolve?.(response);
      } catch (e) {
        log.warn('Error in deleteFundingAccount request\n', e);
        yield put(actions.deleteFundingAccountFailure(e));
        action.meta?.reject?.(e);
      }
    },
  );
}

export default [
  getFundingAccountsActionWatcher,
  createFundingAccountActionWatcher,
  confirmFundingAccountActionWatcher,
  verifyMicroDespositsActionWatcher,
  deleteFundingAccountActionWatcher,
];
